// Layouts del recorrido: pantalla de acceso partida (A1, A4) y pantalla sencilla con barra superior (A3, A5–A7, C2, C3, D1–D3).
import React from 'react';
import { Box } from '@mui/material';
import { J } from '../journeyTokens';
import { BrandMark, Eyebrow, JourneyButton } from './JourneyBasics';

const GRID_BG = 'linear-gradient(rgba(148,163,184,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(148,163,184,.045) 1px,transparent 1px)';

export interface AuthBenefit { title: string; text: string; icon?: React.ReactNode }

export const AuthSplitLayout: React.FC<{
  eyebrow?: string;
  tagline?: string;
  benefits?: AuthBenefit[];
  // Contenido propio del panel de marca bajo el texto (p. ej. los pasos del alta en A2).
  brandExtra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ eyebrow = 'Prueba gratuita · 30 días', tagline = 'Datos comerciales convertidos en contexto para decidir.', benefits = [], brandExtra, children }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', minHeight: '100vh', width: '100%', bgcolor: J.bg, color: J.text, fontFamily: J.font }}>
    <Box component="aside" sx={{ flex: '1 1 520px', minWidth: 0, bgcolor: J.panel, borderRight: `1px solid ${J.border}`, p: { xs: '32px 24px', md: '48px 64px' }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '48px', backgroundImage: GRID_BG, backgroundSize: '56px 56px' }}>
      <BrandMark />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '28px', maxWidth: 520 }}>
        <Eyebrow both>{eyebrow}</Eyebrow>
        <Box sx={{ fontSize: { xs: 56, md: 88 }, lineHeight: 0.95, fontWeight: 800, letterSpacing: '-.065em', color: J.amber }}>PRICING</Box>
        <Box component="p" sx={{ m: 0, fontSize: 18, lineHeight: 1.6, color: J.muted }}>{tagline}</Box>
        {benefits.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {benefits.map((b) => (
              <Box key={b.title} sx={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <Box component="span" sx={{ flex: '0 0 28px', height: 28, borderRadius: '8px', bgcolor: 'rgba(56,189,248,.12)', border: '1px solid rgba(56,189,248,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: J.skyText, fontSize: 14 }}>
                  {b.icon ?? '✓'}
                </Box>
                <Box>
                  <Box component="p" sx={{ m: 0, fontSize: 16, fontWeight: 700 }}>{b.title}</Box>
                  <Box component="p" sx={{ m: 0, fontSize: 13, lineHeight: 1.5, color: J.muted }}>{b.text}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {brandExtra}
      </Box>
      <Box sx={{ display: 'flex', gap: '20px', fontSize: 12, color: J.muted, '& a': { color: J.muted, textDecoration: 'none' } }}>
        <span>© {new Date().getFullYear()} PROA</span>
        <a href="/terms">Términos</a>
        <a href="/privacy">Privacidad</a>
      </Box>
    </Box>
    <Box component="main" sx={{ flex: '1 1 480px', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: '48px 24px' }}>
      <Box sx={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: '24px' }}>{children}</Box>
    </Box>
  </Box>
);

const initials = (name?: string | null) =>
  (name || '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');

export const Avatar: React.FC<{ name?: string | null }> = ({ name }) => (
  <Box component="span" sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: J.border, border: `1px solid ${J.borderStrong}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#e2e8f0', flex: '0 0 36px' }}>
    {initials(name)}
  </Box>
);

export const SimpleLayout: React.FC<{
  userLabel?: string | null;
  onSignOut?: () => void;
  narrow?: boolean;
  children: React.ReactNode;
}> = ({ userLabel, onSignOut, narrow, children }) => (
  <Box sx={{ minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column', bgcolor: J.bg, color: J.text, fontFamily: J.font }}>
    <Box component="header" sx={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, md: '40px' }, borderBottom: `1px solid ${J.border}`, bgcolor: J.panel }}>
      <BrandMark />
      {(userLabel || onSignOut) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: 13, color: J.body }}>
          {userLabel && <Avatar name={userLabel} />}
          {userLabel && <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{userLabel}</Box>}
          {onSignOut && <JourneyButton kind="ghost" small onClick={onSignOut}>Cerrar sesión</JourneyButton>}
        </Box>
      )}
    </Box>
    <Box component="main" sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', p: { xs: '32px 16px', md: '56px 24px' } }}>
      <Box sx={{ width: '100%', maxWidth: narrow ? 560 : 1040, display: 'flex', flexDirection: 'column', gap: narrow ? '24px' : '28px' }}>{children}</Box>
    </Box>
  </Box>
);
